class ApiConfigFactory {
    /**
   * @param {string} serverRoute - The base route for the API (e.g., 'patients', 'visits').
   * @param {string} title - The human-readable title for the configuration.
   * @param {string} [parentIDName=null] - The name of the parent ID parameter.
   * @param {number|string} [parentIDValue=null] - The value of the parent ID parameter.
   */
  constructor(serverRoute, title, parentIDName = null, parentIDValue = null) {
    this.serverRoute = serverRoute;
    this.title = title;
    this.formTitle = '';
    this.baseEndpoint = `/hospital-portal/api/${serverRoute}`;
    this.fetchEndpoint = `${this.baseEndpoint}`;
    this.createEndpoint = `${this.baseEndpoint}`;
    this.updateEndpoint = `${this.baseEndpoint}`;
    this.deleteEndpoint = `${this.baseEndpoint}`;
    this.defaultSortFieldId = 'id';
    this.defaultSortAsc = false;
    this.fields = [];
    this.columns = [];
    this.tabs = [];
    this.dropdownFields = [];
    this.staticDropdownFields = [];
    this.bulkActions = [];
    this.conditionalStyles = {};
    this.useLocalCaching = false;
    this.systemFilters = [];
    this.gcBucketName = 'sh-patient-files';
    this.gcBucketIdentifier = serverRoute.includes('patients')?'patient_id':'';
    this.allowAdd = true;
    this.allowEdit = true;
    this.allowDelete = true;
    this.allowExport = false;
    this.expandableRows = 'Off'; // Default value
    this.expandableRowsComponent = null; // Default value;
    this.onEdit = null; // Custom edit handler (completely overrides default)
    this.onEditEnhance = null; // Data enhancement hook (runs before normal edit)
    this.onBeforeAdd = null; // Async hook before add form opens; return false to cancel, object to pre-fill
    this.pageSize = 50; // Default page size
    this.paginationRowsPerPageOptions = [10, 25, 50, 100]; // Default pagination options
    this.aiActions = []; // AI action buttons per tab
    this.tabAlerts = []; // Alert banners per tab

    // Append parent ID to fetchEndpoint, if provided
    if (parentIDName && parentIDValue) {
      this.appendQueryParam(parentIDName, parentIDValue);
    }
  }

  // Add a form field
  addField(field) {
    this.fields.push(field);
    return this;
  }

  // Add a data table column
  addColumn(column) {
    this.columns.push(column);
    return this;
  }

  // Add a tab for form organization
  // Supports both legacy roles format and new securityPath format:
  // Legacy: { name: 'TabName', roles: 'Admin,Manager' }
  // New: { name: 'TabName', securityPath: '/path', permission: 'View' }
  addTab(tab) {
    this.tabs.push(tab);
    return this;
  }

  // Add a dropdown field
  addDropdownField(field) {
    this.dropdownFields.push(field);
    return this;
  }

  // Add a static dropdown field
  addStaticDropdownField(field) {
    this.staticDropdownFields.push(field);
    return this;
  }

  // Add bulk actions
  addBulkAction(action) {
    this.bulkActions.push(action);
    return this;
  }

  // Set endpoints dynamically
  setEndpoints({ fetch, create, update, delete: del }) {
    if (fetch) this.fetchEndpoint = fetch;
    if (create) this.createEndpoint = create;
    if (update) this.updateEndpoint = update;
    if (del) this.deleteEndpoint = del;
    return this;
  }

  // Append query parameters to fetchEndpoint
  appendQueryParam(key, value) {
    const separator = this.fetchEndpoint.includes('?') ? '&' : '?';
    this.fetchEndpoint = `${this.fetchEndpoint}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    return this; // Allow method chaining
  }

  // Set conditional styling for a column
  addConditionalStyle(columnName, conditions) {
    this.conditionalStyles[columnName] = conditions;
    return this;
  }

  // Set edit enhancement hook (for extending dropdown options, etc.)
  // This hook is called BEFORE the normal edit form opens, allowing you to modify
  // component state (like dropdown options) based on the record being edited
  setEditEnhanceHook(callback) {
    this.onEditEnhance = callback;
    return this;
  }

  // Set custom edit handler (completely overrides default edit behavior)
  // This completely bypasses the FormModal and allows you to implement custom edit forms
  setCustomEditHandler(callback) {
    this.onEdit = callback;
    return this;
  }

  /**
   * Add an AI action button to a specific tab.
   * @param {Object} action - AI action config
   * @param {string} action.tab - Which tab the button appears on
   * @param {string} action.featureKey - Feature identifier (e.g., 'treatment-suggestions')
   * @param {string} action.buttonLabel - Button text (e.g., 'AI Assist')
   * @param {string} [action.buttonIcon] - If set, renders a small icon-only button instead of full text
   * @param {string} [action.mode='suggestions'] - 'suggestions' (apply-able) or 'report' (read-only)
   * @param {string} action.endpoint - API endpoint to call
   * @param {Function} action.payloadBuilder - (formData) => request body
   * @param {Object} action.fieldMapping - Maps AI response keys to form field names
   * @param {Array} [action.sections] - Custom sections: [{ key, title, icon }]
   * @param {string} [action.panelTitle] - Custom panel header title
   * @param {string} [action.reasoningKey='reasoning'] - Response key for reasoning/summary
   * @param {string} [action.confidenceKey='confidence'] - Response key for confidence/severity
   * @param {string} action.permissionKey - Permission key required (e.g., '/ai-assist/treatment-suggestions')
   * @param {string} [action.permissionAction='Execute'] - Permission action to check
   */
  addAiAction(action) {
    this.aiActions.push({
      permissionAction: 'Execute',
      ...action,
    });
    return this;
  }

  /**
   * Add an alert banner to a specific tab.
   * @param {Object} alert - Tab alert config
   * @param {string} alert.tab - Which tab the alert appears on. Use '*' to show it on every tab.
   * @param {Function} alert.getMessage - (formData) => string | null
   */
  addTabAlert(alert) {
    this.tabAlerts.push(alert);
    return this;
  }
}

export default ApiConfigFactory;
